import React, { Suspense, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ToastContainer } from "react-toastify";
import { ErrorBoundary, LocationAwareErrorBoundary } from "../components/ErrorBoundary";
import { useAppTheme } from "../ThemeProvider";
import { ActivityTrackingProvider } from "../contexts/ActivityTrackingContext";
import { PerformanceProvider } from "../contexts/PerformanceContext";
import { initCoreWebVitals } from "../utils/performance";
import { AppRoutes } from "./routes";
import { hasAnalyticsConsent } from "../services/cookieService";
import { initializeAnalytics } from "../firebase";
import CookieConsentBanner from "../components/cookie-consent/CookieConsentBanner";
import CookiePreferencesDialog from "../components/cookie-consent/CookiePreferencesDialog";
import brandConfig from "../config/brandConfig";
import "react-toastify/dist/ReactToastify.css";
import "../i18n";
import "../App.css";

const LoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "100vh",
    }}
  >
    <CircularProgress />
  </Box>
);


const AppContent: React.FC = () => {
  const { theme } = useAppTheme();

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(theme).forEach(([key, value]) => {
      if (typeof value === "string") {
        root.style.setProperty(key, value);
      }
    });
  }, [theme]);

  // Initialize analytics services if consent was already granted (deferred)
  useEffect(() => {
    const hasConsent = hasAnalyticsConsent();
    if (hasConsent) {
      // Defer analytics initialization to reduce initial load time
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          initializeAnalytics(true).catch((error) => {
            console.error("[App][initializeAnalytics] - Error:", error);
          });
        }, { timeout: 2000 });
      } else {
        setTimeout(() => {
          initializeAnalytics(true).catch((error) => {
            console.error("[App][initializeAnalytics] - Error:", error);
          });
        }, 2000);
      }
    }
  }, []);

  // Initialize Core Web Vitals tracking (only if analytics consent is granted)
  useEffect(() => {
    const hasConsent = hasAnalyticsConsent();
    const isEnabled =
      hasConsent &&
      (process.env.NODE_ENV === "production" ||
        process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === "true");

    if (isEnabled) {
      initCoreWebVitals({
        enabled: true,
      });
    }
  }, []);

  // Keep runtime metadata correct even if a build is served from the wrong
  // white-label hostname and the hostname guardrail changes the active brand.
  useEffect(() => {
    document.title = brandConfig.pageTitle;

    const link = document.querySelector("link[rel~='icon']");
    if (link) (link as HTMLLinkElement).href = `/${brandConfig.favicon}`;

    const appleLink = document.querySelector("link[rel='apple-touch-icon']");
    if (appleLink) (appleLink as HTMLLinkElement).href = `/${brandConfig.touchIcon}`;

    const description = document.querySelector("meta[name='description']");
    if (description && brandConfig.description) {
      description.setAttribute('content', brandConfig.description);
    }
  }, []);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <AppRoutes />
    </Suspense>
  );
};

export const App: React.FC = () => {
  const isPerformanceMonitoringEnabled =
    process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === "true";

  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <LocationAwareErrorBoundary>
          <ActivityTrackingProvider>
            <PerformanceProvider enabled={isPerformanceMonitoringEnabled}>
              <CssBaseline />
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
              <AppContent />
              <CookieConsentBanner />
              <CookiePreferencesDialog />
            </PerformanceProvider>
          </ActivityTrackingProvider>
        </LocationAwareErrorBoundary>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
